import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

const Slide = ({ src, alt, zoomed, onToggle }) => {
  const ref = useRef(null);
  const point = useRef({ x: 0.5, y: 0.5 });
  useEffect(() => {
    const el = ref.current;
    if (zoomed && el) {
      el.scrollTo({ left: point.current.x * el.scrollWidth - el.clientWidth / 2, top: point.current.y * el.scrollHeight - el.clientHeight / 2 });
    }
    point.current = { x: 0.5, y: 0.5 };
  }, [zoomed]);
  const toggle = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    point.current = { x: (e.clientX - box.left) / box.width, y: (e.clientY - box.top) / box.height };
    onToggle();
  };
  return (
    <div ref={ref} data-lenis-prevent className={`h-full w-full shrink-0 snap-center ${zoomed ? "overflow-auto" : "flex items-center justify-center overflow-hidden"}`}>
      <img src={src} alt={alt} draggable={false} onClick={toggle}
        className={zoomed ? "h-[200%] w-[200%] max-w-none cursor-zoom-out object-contain" : "max-h-full max-w-full cursor-zoom-in select-none object-contain"} />
    </div>
  );
};

export const Lightbox = ({ images, start = 0, alt, onClose }) => {
  const ref = useRef(null);
  const [index, setIndex] = useState(start);
  const [zoomed, setZoomed] = useState(false);
  const many = images.length > 1;

  const go = (i) => {
    const el = ref.current;
    if (!el) return;
    setZoomed(false);
    el.scrollTo({ left: ((i + images.length) % images.length) * el.clientWidth, behavior: "smooth" });
  };

  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTo({ left: start * el.clientWidth });
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [start]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(index + 1);
      if (e.key === "ArrowLeft") go(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const btn = "flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20";
  return createPortal(
    <div data-testid="photo-lightbox" role="dialog" aria-modal="true" aria-label={`Fotos de ${alt}`} className="no-invert fixed inset-0 z-[100] flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3">
        <span data-testid="lightbox-counter" className="mono text-xs text-zinc-300">{index + 1} / {images.length}</span>
        <div className="flex gap-2">
          <button data-testid="lightbox-zoom" onClick={() => setZoomed((z) => !z)} aria-label={zoomed ? "Diminuir zoom" : "Aumentar zoom"} className={btn}>
            {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
          </button>
          <button data-testid="lightbox-close" onClick={onClose} aria-label="Fechar" className={btn}><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="relative min-h-0 flex-1">
        <div ref={ref} data-lenis-prevent onScroll={(e) => setIndex(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
          className={`no-scrollbar flex h-full snap-x snap-mandatory ${zoomed ? "overflow-x-hidden" : "overflow-x-auto"}`}>
          {images.map((src, i) => (
            <Slide key={i} src={src} alt={`${alt} - foto ${i + 1}`} zoomed={zoomed && i === index} onToggle={() => setZoomed((z) => !z)} />
          ))}
        </div>
        {many && !zoomed && (
          <>
            <button data-testid="lightbox-prev" onClick={() => go(index - 1)} aria-label="Foto anterior" className={`absolute left-3 top-1/2 -translate-y-1/2 ${btn}`}><ChevronLeft className="h-5 w-5" /></button>
            <button data-testid="lightbox-next" onClick={() => go(index + 1)} aria-label="Próxima foto" className={`absolute right-3 top-1/2 -translate-y-1/2 ${btn}`}><ChevronRight className="h-5 w-5" /></button>
          </>
        )}
      </div>
      <p className="py-3 text-center text-xs text-zinc-400">{zoomed ? "Arraste para ver os detalhes · toque para sair do zoom" : "Toque na foto para dar zoom"}</p>
    </div>,
    document.body,
  );
};
